"use client";

import type { ReactNode, RefObject } from "react";
import type { LeadFlowStepErrors } from "../types";

import { createContext, useContext } from "react";
import { NO_ERRORS } from "../constants/empty-values";

type LeadFlowStepContextValue = {
  /** Moves to the next step, as Continue does. */
  advance: () => void;
  errors: LeadFlowStepErrors;
  /**
   * The first step sits under the section headline, so its question renders
   * as a subheading; every later step owns its heading.
   */
  isFirstStep: boolean;
  /**
   * Whether a question's answer is still resolving, for the form's Enter
   * handler. A ref, so a pending answer never re-renders the whole flow.
   */
  isAnswerPendingRef?: RefObject<boolean>;
  /**
   * An inert copy of a step, shown under a surface or for sizing layout.
   * Questions render as usual but write nothing and fetch nothing.
   */
  isInert?: boolean;
  /**
   * A question whose answer is still resolving, such as the postal code
   * waiting on its office lookup, holds Continue and submit until it settles.
   */
  setIsAnswerPending?: (isAnswerPending: boolean) => void;
};

const LeadFlowStepContext = createContext<LeadFlowStepContextValue>({
  advance: () => undefined,
  errors: NO_ERRORS,
  isFirstStep: true,
});

type LeadFlowStepProviderProps = {
  children: ReactNode;
  value: LeadFlowStepContextValue;
};

function LeadFlowStepProvider({ children, value }: LeadFlowStepProviderProps) {
  return (
    <LeadFlowStepContext.Provider value={value}>
      {children}
    </LeadFlowStepContext.Provider>
  );
}

function useLeadFlowStep(): LeadFlowStepContextValue {
  return useContext(LeadFlowStepContext);
}

export { LeadFlowStepProvider, useLeadFlowStep };
